import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Menu, type MenuAction } from '../../components/Menu';
import { Modal } from '../../components/ui';
import { OwnerPickerModal } from '../../components/People';
import { useToast } from '../../components/Toast';
import { DeleteDialog } from '../../components/DeleteDialog';
import { ActivityComposer } from './ActivityComposer';

/**
 * الإجراءات السريعة على عميل.
 *
 * أخفّ ممّا للعميل المحتمل: العميل ليس له حالة ولا تصنيف يتغيّران بنقرة،
 * وتعطيله قرارٌ له موضعه في ملفه لا في قائمة سريعة — إخراج عميل من التعامل
 * يُقرأ من صفحته كاملةً لا من صفّ في جدول.
 */

type Mode = 'owner' | 'activity' | 'delete' | null;

export interface QuickAccount {
  id: string;
  name: string;
  ownerId?: string | null;
  email?: string | null;
  phone?: string | null;
}

export function AccountQuickActions({
  account,
  onChanged,
  className = 'btn btn-sm btn-icon btn-ghost',
  showOpen = true,
}: {
  account: QuickAccount;
  onChanged: () => void;
  className?: string;
  showOpen?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(null);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/crm/accounts/${account.id}`, { method: 'PATCH', body }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries();
      setMode(null);
      toast(
        variables.ownerId
          ? `نُقل «${account.name}» إلى مسؤول جديد`
          : `أُلغي إسناد «${account.name}»`,
      );
      onChanged();
    },
  });

  const actions: MenuAction[] = [
    {
      key: 'owner',
      label: 'تغيير المسؤول',
      icon: 'users',
      onSelect: () => setMode('owner'),
    },
    {
      key: 'call',
      label: 'اتصال',
      icon: 'phone',
      disabled: !account.phone,
      title: account.phone ?? 'لا رقم مسجَّل',
      onSelect: () => {
        navigate(`/callcenter?dial=${encodeURIComponent(account.phone ?? '')}`);
      },
    },
    {
      key: 'mail',
      label: 'إرسال بريد',
      icon: 'mail',
      disabled: !account.email,
      title: account.email ?? 'لا بريد مسجَّل',
      onSelect: () => {
        window.location.href = `mailto:${account.email}`;
      },
    },
    {
      key: 'activity',
      label: 'تسجيل نشاط',
      icon: 'clipboard',
      separated: true,
      onSelect: () => setMode('activity'),
    },
  ];

  if (showOpen) {
    actions.push({
      key: 'open',
      label: 'فتح الملف',
      icon: 'arrowLeft',
      separated: true,
      onSelect: () => navigate(`/crm/accounts/${account.id}`),
    });
  }

  if (atLeast('MANAGER')) {
    actions.push({
      key: 'delete',
      label: 'حذف',
      icon: 'trash',
      danger: true,
      separated: !showOpen,
      onSelect: () => setMode('delete'),
    });
  }

  return (
    <>
      <Menu actions={actions} label={`إجراءات ${account.name}`} className={className} />

      {mode === 'owner' && (
        <OwnerPickerModal
          currentId={account.ownerId ?? null}
          title={`مسؤول ${account.name}`}
          pending={patch.isPending}
          error={patch.error}
          onPick={(userId) => patch.mutate({ ownerId: userId })}
          onClose={() => setMode(null)}
        />
      )}

      {mode === 'activity' && (
        <Modal title={`نشاط على ${account.name}`} onClose={() => setMode(null)}>
          <ActivityComposer
            link={{ accountId: account.id }}
            onDone={() => {
              void queryClient.invalidateQueries();
              setMode(null);
              onChanged();
            }}
          />
        </Modal>
      )}

      {mode === 'delete' && (
        <DeleteDialog
          entity="account"
          id={account.id}
          endpoint={`/crm/accounts/${account.id}`}
          onClose={() => setMode(null)}
          onDeleted={() => {
            setMode(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}
